/**
 * Role-Based Access Control (RBAC) Configuration
 *
 * Defines all user roles and their associated permissions across the application.
 * Used for route protection, UI element visibility, and feature access control.
 */

/**
 * Available user roles in the system
 */
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  COMPANY_ADMIN: 'COMPANY_ADMIN',
  COMPLIANCE_MANAGER: 'COMPLIANCE_MANAGER',
  PROJECT_MANAGER: 'PROJECT_MANAGER',
  MANAGEMENT: 'MANAGEMENT',
  EXECUTIVE: 'EXECUTIVE',
  EMPLOYEE: 'EMPLOYEE',
}

/**
 * Role hierarchy levels (higher number = more privileges)
 */
export const ROLE_HIERARCHY = {
  [ROLES.SUPER_ADMIN]: 5,
  [ROLES.COMPANY_ADMIN]: 4,
  [ROLES.COMPLIANCE_MANAGER]: 3,
  [ROLES.PROJECT_MANAGER]: 3,
  [ROLES.EXECUTIVE]: 2,
  [ROLES.MANAGEMENT]: 2,
  [ROLES.EMPLOYEE]: 1,
}

/**
 * Permission definitions for each role
 */
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: {
    // Full system access
    canAccessCompanies: true,
    canManageCompanies: true,
    canDeleteCompanies: true,

    // Finance & Billing
    canAccessFinance: true,
    canManageFinance: true,
    canViewAllInvoices: true,
    canManagePayments: true,

    // Reports
    canAccessReports: true,
    canViewSystemReports: true,
    canExportReports: true,

    // Package Management
    canAccessPackages: true,
    canManagePackages: true,
    canAssignPackages: true,
    canDeletePackages: true,

    // Certificate Management
    canAccessCertificates: true,
    canManageCertificates: true,
    canDeleteCertificates: true,

    // User Management
    canAccessUsers: true,
    canManageUsers: true,
    canDeleteUsers: true,
    canAssignRoles: true,

    // Role Management
    canAccessRoles: true,
    canManageRoles: true,
    canAssignPermissions: true,

    // System Settings
    canAccessSettings: true,
    canManageSettings: true,
    canConfigureSystem: true,

    // Dashboard
    canAccessDashboard: true,
    canViewSystemStats: true,
    canViewAllCompanyData: true,
  },

  [ROLES.COMPANY_ADMIN]: {
    // Company-level access
    canAccessCompanies: true,
    canManageOwnCompany: true,
    canViewCompanyData: true,

    // Finance (limited to own company)
    canAccessFinance: true,
    canViewOwnInvoices: true,
    canRequestPayments: true,

    // Reports (own company)
    canAccessReports: true,
    canViewCompanyReports: true,
    canExportReports: true,

    // Projects
    canAccessProjects: true,
    canManageProjects: true,
    canAssignProjectManagers: true,

    // Workflows
    canAccessWorkflows: true,
    canManageWorkflows: true,
    canDeleteWorkflows: true,

    // Users (within company)
    canAccessUsers: true,
    canManageCompanyUsers: true,
    canInviteUsers: true,

    // Certificates (own company)
    canAccessCertificates: true,
    canViewCompanyCertificates: true,
    canRequestCertificates: true,

    // Dashboard
    canAccessDashboard: true,
    canViewCompanyStats: true,
  },

  [ROLES.COMPLIANCE_MANAGER]: {
    // Compliance-focused access
    canAccessProjects: true,
    canManageProjects: true,
    canCreateProjects: true,
    canViewProjectData: true,

    // Compliance-specific permissions
    canManageCompliance: true,
    canCreateChecklists: true,
    canSubmitForApproval: true,

    // Workflows
    canAccessWorkflows: true,
    canViewWorkflows: true,

    // Reports (company-level)
    canAccessReports: true,
    canViewCompanyReports: true,
    canExportReports: true,

    // Certificates
    canAccessCertificates: true,
    canViewCompanyCertificates: true,
    canRequestCertificates: true,

    // Users (view only)
    canAccessUsers: true,
    canViewCompanyUsers: true,

    // Dashboard
    canAccessDashboard: true,
    canViewCompanyStats: true,
  },

  [ROLES.PROJECT_MANAGER]: {
    // Project-level access
    canAccessProjects: true,
    canManageAssignedProjects: true,
    canViewProjectData: true,

    // Tasks & Teams
    canAccessTasks: true,
    canManageTasks: true,
    canAssignTasks: true,
    canViewTeamMembers: true,

    // Reports (project-level)
    canAccessReports: true,
    canViewProjectReports: true,
    canExportProjectReports: true,

    // Certificates (project-level)
    canAccessCertificates: true,
    canViewProjectCertificates: true,
    canRequestCertificates: true,

    // Dashboard
    canAccessDashboard: true,
    canViewProjectStats: true,
  },

  [ROLES.EXECUTIVE]: {
    // Executive-level access (strategic oversight)
    canAccessProjects: true,
    canViewAllProjects: true,
    canViewAssignedProjects: true,

    // Strategic decisions
    canMakeDecisions: true,
    canExcuseItems: true,
    canViewStrategicMetrics: true,

    // Tasks (view only)
    canAccessTasks: true,
    canViewTeamTasks: true,

    // Reports (comprehensive)
    canAccessReports: true,
    canViewReports: true,
    canViewCompanyReports: true,
    canExportReports: true,

    // Certificates (view)
    canAccessCertificates: true,
    canViewCompanyCertificates: true,

    // Dashboard
    canAccessDashboard: true,
    canViewCompanyStats: true,
    canViewRiskSummary: true,
  },

  [ROLES.MANAGEMENT]: {
    // Management-level access
    canAccessProjects: true,
    canViewAssignedProjects: true,

    // Tasks
    canAccessTasks: true,
    canManageOwnTasks: true,
    canViewTeamTasks: true,

    // Reports (limited)
    canAccessReports: true,
    canViewOwnReports: true,

    // Certificates (view only)
    canAccessCertificates: true,
    canViewOwnCertificates: true,

    // Dashboard
    canAccessDashboard: true,
    canViewOwnStats: true,
  },

  [ROLES.EMPLOYEE]: {
    // Basic employee access
    canAccessTasks: true,
    canViewOwnTasks: true,
    canUpdateTaskStatus: true,

    // Certificates (view only)
    canAccessCertificates: true,
    canViewOwnCertificates: true,

    // Profile
    canAccessProfile: true,
    canUpdateOwnProfile: true,

    // Dashboard
    canAccessDashboard: true,
    canViewOwnStats: true,
  },
}

/**
 * Helper function to check if a role has a specific permission
 * @param {string} role - User role
 * @param {string} permission - Permission to check
 * @returns {boolean} True if role has permission
 */
export const hasPermission = (role, permission) => {
  if (!role || !permission) return false
  const permissions = ROLE_PERMISSIONS[role]
  return permissions ? permissions[permission] === true : false
}

/**
 * Helper function to check if a role is higher in hierarchy than another
 * @param {string} role1 - First role
 * @param {string} role2 - Second role
 * @returns {boolean} True if role1 is higher than role2
 */
export const isHigherRole = (role1, role2) => {
  return ROLE_HIERARCHY[role1] > ROLE_HIERARCHY[role2]
}

/**
 * Helper function to get all permissions for a role
 * @param {string} role - User role
 * @returns {Object} Object containing all permissions
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || {}
}

/**
 * Helper function to check if user has any of the provided roles
 * @param {string} userRole - User's current role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean} True if user role is in allowed roles
 */
export const hasAnyRole = (userRole, allowedRoles = []) => {
  return allowedRoles.includes(userRole)
}

/**
 * Helper function to get role display name
 * @param {string} role - Role identifier
 * @returns {string} Human-readable role name
 */
export const getRoleDisplayName = (role) => {
  const roleNames = {
    [ROLES.SUPER_ADMIN]: 'Super Admin',
    [ROLES.COMPANY_ADMIN]: 'Company Admin',
    [ROLES.COMPLIANCE_MANAGER]: 'Compliance Manager',
    [ROLES.PROJECT_MANAGER]: 'Project Manager',
    [ROLES.EXECUTIVE]: 'Executive',
    [ROLES.MANAGEMENT]: 'Management',
    [ROLES.EMPLOYEE]: 'Employee',
  }
  return roleNames[role] || role
}

/**
 * Export default configuration
 */
export default {
  ROLES,
  ROLE_HIERARCHY,
  ROLE_PERMISSIONS,
  hasPermission,
  isHigherRole,
  getRolePermissions,
  hasAnyRole,
  getRoleDisplayName,
}
